import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Collapse,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CodeIcon from '@mui/icons-material/Code';
import moment from 'moment';
import Iconify from 'src/components/iconify';
import { renderSuccessMessage } from 'src/lib/toastMessage';

const formatDateTime = (val) => {
  if (!val) return '—';
  const m = typeof val === 'number' ? (val < 1e11 ? moment.unix(val) : moment(val)) : moment(val);
  return m.isValid() ? m.format('DD MMM YYYY, hh:mm:ss A') : String(val);
};

export default function AdminSettingDetailsModal({ open, onClose, setting }) {
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!setting) return null;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    renderSuccessMessage('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const {
    seq_id,
    _id,
    campaignIds = [],
    selectedUserId,
    email,
    requested_by,
    create_date,
    status,
    updateExistingRecords,
    A_required,
    NA_required,
    BZ_required,
    O_required,
    IVR_required,
    DTMF_required,
    CallDuration_required,
    DateTime_required,
    old_data = {},
  } = setting;

  const isPending = status === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Admin Setting #{seq_id ?? '—'}
          </Typography>
          <Chip
            size="small"
            icon={isPending ? <HourglassEmptyIcon sx={{ fontSize: 16 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            label={isPending ? 'Pending' : 'Processed'}
            color={isPending ? 'warning' : 'success'}
            variant="soft"
            sx={{ fontWeight: 600 }}
          />
          {updateExistingRecords ? (
            <Chip size="small" label="Update Existing Records" color="primary" variant="soft" sx={{ fontWeight: 600 }} />
          ) : (
            <Chip size="small" label="New Records Only" variant="outlined" sx={{ fontWeight: 500 }} />
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2.5 }}>
        {/* Basic Meta Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                CAMPAIGN ID(S)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {campaignIds.length > 0 ? (
                  campaignIds.map((cid) => (
                    <Chip
                      key={cid}
                      label={cid}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontFamily: 'monospace' }}
                      onClick={() => handleCopy(cid)}
                      icon={<ContentCopyIcon sx={{ fontSize: '12px !important' }} />}
                    />
                  ))
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                SELECTED USER
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5, fontFamily: 'monospace' }}>
                {selectedUserId || '—'}
              </Typography>
              {email && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {email}
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                REQUESTED BY
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {requested_by || '—'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                CREATED AT
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {formatDateTime(create_date)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Requested Updates &amp; Snapshot Comparison
        </Typography>

        {/* 1. Answer Count (A_required) */}
        {A_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="Answer Count (A)" color="primary" size="small" sx={{ fontWeight: 700 }} />
              <Typography variant="caption" color="text.secondary">
                Mode: <strong>{A_required.countMode || 'count'}</strong>
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
                    NEW TARGET CONFIGURATION
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Count: <strong>{A_required.count}</strong> {A_required.countMode === 'percent' ? '%' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Adjusted To:{' '}
                    {A_required.adjustedTo?.length > 0
                      ? A_required.adjustedTo.map((t) => (
                          <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                        ))
                      : 'None'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS SNAPSHOT (OLD DATA)
                  </Typography>
                  {old_data?.A_required ? (
                    typeof old_data.A_required === 'object' && !('count' in old_data.A_required) ? (
                      Object.entries(old_data.A_required).map(([cId, info]) => (
                        <Typography key={cId} variant="body2" sx={{ mt: 0.5 }}>
                          Campaign #{cId}: <strong>{info?.count ?? '—'}</strong>
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Count: <strong>{old_data.A_required?.count ?? '—'}</strong>
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 2. Not Answer Count (NA_required) */}
        {NA_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="Not Answer Count (NA)" color="warning" size="small" sx={{ fontWeight: 700 }} />
              <Typography variant="caption" color="text.secondary">
                Mode: <strong>{NA_required.countMode || 'count'}</strong>
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>
                    NEW TARGET CONFIGURATION
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Count: <strong>{NA_required.count}</strong> {NA_required.countMode === 'percent' ? '%' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Adjusted To:{' '}
                    {NA_required.adjustedTo?.length > 0
                      ? NA_required.adjustedTo.map((t) => (
                          <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                        ))
                      : 'None'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS SNAPSHOT (OLD DATA)
                  </Typography>
                  {old_data?.NA_required ? (
                    typeof old_data.NA_required === 'object' && !('count' in old_data.NA_required) ? (
                      Object.entries(old_data.NA_required).map(([cId, info]) => (
                        <Typography key={cId} variant="body2" sx={{ mt: 0.5 }}>
                          Campaign #{cId}: <strong>{info?.count ?? '—'}</strong>
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Count: <strong>{old_data.NA_required?.count ?? '—'}</strong>
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 3. Busy Count (BZ_required) */}
        {BZ_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="Busy Count (BZ)" color="default" size="small" sx={{ fontWeight: 700 }} />
              <Typography variant="caption" color="text.secondary">
                Mode: <strong>{BZ_required.countMode || 'count'}</strong>
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    NEW TARGET CONFIGURATION
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Count: <strong>{BZ_required.count}</strong> {BZ_required.countMode === 'percent' ? '%' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Adjusted To:{' '}
                    {BZ_required.adjustedTo?.length > 0
                      ? BZ_required.adjustedTo.map((t) => (
                          <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                        ))
                      : 'None'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS SNAPSHOT (OLD DATA)
                  </Typography>
                  {old_data?.BZ_required ? (
                    typeof old_data.BZ_required === 'object' && !('count' in old_data.BZ_required) ? (
                      Object.entries(old_data.BZ_required).map(([cId, info]) => (
                        <Typography key={cId} variant="body2" sx={{ mt: 0.5 }}>
                          Campaign #{cId}: <strong>{info?.count ?? '—'}</strong>
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Count: <strong>{old_data.BZ_required?.count ?? '—'}</strong>
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 4. Other Count (O_required) */}
        {O_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="Other Count (O)" color="default" size="small" sx={{ fontWeight: 700 }} />
              <Typography variant="caption" color="text.secondary">
                Mode: <strong>{O_required.countMode || 'count'}</strong>
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    NEW TARGET CONFIGURATION
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Count: <strong>{O_required.count}</strong> {O_required.countMode === 'percent' ? '%' : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Adjusted To:{' '}
                    {O_required.adjustedTo?.length > 0
                      ? O_required.adjustedTo.map((t) => (
                          <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                        ))
                      : 'None'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS SNAPSHOT (OLD DATA)
                  </Typography>
                  {old_data?.O_required ? (
                    typeof old_data.O_required === 'object' && !('count' in old_data.O_required) ? (
                      Object.entries(old_data.O_required).map(([cId, info]) => (
                        <Typography key={cId} variant="body2" sx={{ mt: 0.5 }}>
                          Campaign #{cId}: <strong>{info?.count ?? '—'}</strong>
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Count: <strong>{old_data.O_required?.count ?? '—'}</strong>
                      </Typography>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 5. IVR Update (IVR_required) */}
        {IVR_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="IVR Update" color="info" size="small" sx={{ fontWeight: 700 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="info.main" sx={{ fontWeight: 700 }}>
                    NEW TARGET IVR
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    IVR ID: <strong>{IVR_required.ivrId ?? '—'}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    IVR Title: <strong>{IVR_required.ivrTitle || '—'}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    IVR Type: <strong>{IVR_required.ivrType || '—'}</strong>
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS IVR SNAPSHOT
                  </Typography>
                  {old_data?.IVR_required ? (
                    typeof old_data.IVR_required === 'object' && !('ivrId' in old_data.IVR_required) ? (
                      Object.entries(old_data.IVR_required).map(([cId, info]) => (
                        <Box key={cId} sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            Campaign #{cId}: ID <strong>{info?.ivrId}</strong> ({info?.ivrTitle || '—'})
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          IVR ID: <strong>{old_data.IVR_required?.ivrId ?? '—'}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          IVR Title: <strong>{old_data.IVR_required?.ivrTitle || '—'}</strong>
                        </Typography>
                      </>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 6. DTMF Update (DTMF_required) */}
        {DTMF_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="DTMF Update" color="secondary" size="small" sx={{ fontWeight: 700 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="secondary.main" sx={{ fontWeight: 700 }}>
                    UPDATED / NEW DTMF KEYS
                  </Typography>
                  {DTMF_required.nodes && DTMF_required.nodes.length > 0 ? (
                    DTMF_required.nodes.map((n) => (
                      <Box key={n.node || n.nodeId} sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Node: {n.node || n.nodeId}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                          {n.data && Object.keys(n.data).length > 0 ? (
                            Object.entries(n.data).map(([key, pct]) => (
                              <Chip
                                key={key}
                                label={`Key ${key}: ${pct}%`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No keys defined
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      No nodes specified
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS DTMF PRESS COUNT SNAPSHOT
                  </Typography>
                  {old_data?.DTMF_required?.nodes && old_data.DTMF_required.nodes.length > 0 ? (
                    old_data.DTMF_required.nodes.map((n) => (
                      <Box key={n.node || n.nodeId} sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Node: {n.node || n.nodeId}
                        </Typography>
                        {n.allValidKeys && n.allValidKeys.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Valid IVR Keys: {n.allValidKeys.join(', ')}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {n.valid &&
                            Object.entries(n.valid).map(([k, pct]) => (
                              <Chip
                                key={k}
                                label={`${k}: ${pct}% (Valid)`}
                                size="small"
                                color="success"
                                variant="soft"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))}
                          {n.invalid &&
                            Object.entries(n.invalid).map(([k, pct]) => (
                              <Chip
                                key={k}
                                label={`${k}: ${pct}% (Invalid)`}
                                size="small"
                                color="error"
                                variant="soft"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))}
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 7. Call Duration (CallDuration_required) */}
        {CallDuration_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="Call Duration" color="success" size="small" sx={{ fontWeight: 700 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="success.dark" sx={{ fontWeight: 700 }}>
                    NEW TARGET DURATION &amp; TIME WINDOW
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Duration Range: <strong>{CallDuration_required.min ?? '—'}s</strong> –{' '}
                    <strong>{CallDuration_required.max ?? '—'}s</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Call Start Time: <strong>{formatDateTime(CallDuration_required.callStartTime)}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Call End Time: <strong>{formatDateTime(CallDuration_required.callEndTime)}</strong>
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS DURATION &amp; TIME SNAPSHOT
                  </Typography>
                  {old_data?.CallDuration_required ? (
                    typeof old_data.CallDuration_required === 'object' && !('callStartTime' in old_data.CallDuration_required) ? (
                      Object.entries(old_data.CallDuration_required).map(([cId, info]) => (
                        <Box key={cId} sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Campaign #{cId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Campaign Time: {formatDateTime(info?.campaignDateTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Start: {formatDateTime(info?.callStartTime)} | End: {formatDateTime(info?.callEndTime)}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Call Start: {formatDateTime(old_data.CallDuration_required?.callStartTime)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Call End: {formatDateTime(old_data.CallDuration_required?.callEndTime)}
                        </Typography>
                      </>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 8. DateTime (DateTime_required) */}
        {DateTime_required && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip label="Campaign Date &amp; Time" color="info" size="small" sx={{ fontWeight: 700 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="info.dark" sx={{ fontWeight: 700 }}>
                    NEW TARGET DATES
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Campaign Time: <strong>{formatDateTime(DateTime_required.campaignDateTime)}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Call Start: <strong>{formatDateTime(DateTime_required.callStartTime)}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Call End: <strong>{formatDateTime(DateTime_required.callEndTime)}</strong>
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    PREVIOUS TIME SNAPSHOT
                  </Typography>
                  {old_data?.DateTime_required ? (
                    typeof old_data.DateTime_required === 'object' && !('callStartTime' in old_data.DateTime_required) ? (
                      Object.entries(old_data.DateTime_required).map(([cId, info]) => (
                        <Box key={cId} sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Campaign #{cId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Campaign Time: {formatDateTime(info?.campaignDateTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Start: {formatDateTime(info?.callStartTime)} | End: {formatDateTime(info?.callEndTime)}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Campaign Time: {formatDateTime(old_data.DateTime_required?.campaignDateTime)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Call Start: {formatDateTime(old_data.DateTime_required?.callStartTime)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Call End: {formatDateTime(old_data.DateTime_required?.callEndTime)}
                        </Typography>
                      </>
                    )
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      No prior snapshot recorded
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Collapsible Raw JSON */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<CodeIcon />}
            onClick={() => setShowRawJson((prev) => !prev)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {showRawJson ? 'Hide Raw JSON' : 'View Complete Raw JSON'}
          </Button>
          <Collapse in={showRawJson}>
            <Paper
              sx={{
                p: 2,
                mt: 1,
                borderRadius: 1,
                bgcolor: 'grey.900',
                color: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              <pre style={{ margin: 0 }}>{JSON.stringify(setting, null, 2)}</pre>
            </Paper>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AdminSettingDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setting: PropTypes.object,
};
